/**
 * OIDC / OAuth2 protocol-level constants.
 *
 * Centralizing these avoids magic strings sprinkled across the auth code
 * (`auth-api.ts`, `auth-callback.ts`, `auth.ts`).
 */

// ---------------------------------------------------------
// AuthProvider endpoint paths (relative to OIDC_AUTHORITY)
// ---------------------------------------------------------
export const OIDC_ENDPOINT = {
  AUTHORIZE: '/connect/authorize',
  TOKEN: '/connect/token',
  END_SESSION: '/connect/endsession',
} as const;

// ---------------------------------------------------------
// OAuth2 grant types (used in /connect/token request body)
// ---------------------------------------------------------
export const OAUTH_GRANT_TYPE = {
  AUTHORIZATION_CODE: 'authorization_code',
  REFRESH_TOKEN: 'refresh_token',
} as const;

// ---------------------------------------------------------
// OAuth2 response types (used in /connect/authorize)
// ---------------------------------------------------------
export const OAUTH_RESPONSE_TYPE = {
  CODE: 'code',
} as const;

// ---------------------------------------------------------
// PKCE code-challenge methods
// ---------------------------------------------------------
export const PKCE_CHALLENGE_METHOD = {
  S256: 'S256',
  PLAIN: 'plain',
} as const;

// ---------------------------------------------------------
// OAuth2 / OIDC query parameter names (used in URLs returned
// from the AuthProvider as well as request bodies).
// ---------------------------------------------------------
export const OAUTH_PARAM = {
  CODE: 'code',
  STATE: 'state',
  SCOPE: 'scope',
  SESSION_STATE: 'session_state',
  ISS: 'iss',
  ERROR: 'error',
  ERROR_DESCRIPTION: 'error_description',
  CLIENT_ID: 'client_id',
  REDIRECT_URI: 'redirect_uri',
  RESPONSE_TYPE: 'response_type',
  CODE_CHALLENGE: 'code_challenge',
  CODE_CHALLENGE_METHOD: 'code_challenge_method',
  CODE_VERIFIER: 'code_verifier',
  GRANT_TYPE: 'grant_type',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN_HINT: 'id_token_hint',
  POST_LOGOUT_REDIRECT_URI: 'post_logout_redirect_uri',
} as const;

// ---------------------------------------------------------
// HTTP misc.
// ---------------------------------------------------------
export const FORM_URLENCODED_CONTENT_TYPE = 'application/x-www-form-urlencoded';

// ---------------------------------------------------------
// Misc auth tunables
// ---------------------------------------------------------
/** Number of random bytes used to generate the OAuth `state` value. */
export const OAUTH_STATE_BYTE_LENGTH = 16;

// `SESSION_STORAGE_KEY.CALLBACK_CODE` is intentionally stored in sessionStorage
// (per-tab, short-lived) and is used only to guard against duplicate
// processing of a one-time OAuth `code` when the user refreshes the tab.
export const SESSION_STORAGE_KEY = {
  CALLBACK_CODE: 'oauth_callback_code',
} as const;
